import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { useToast } from "../ui/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Input } from "../ui/input";
import { Spinner } from "../ui/Spinner";
import { subModuleService } from "../../services/subModuleService";
import { SubModule } from "types/subModule";
import { questionService } from "../../services/questionService";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";

interface InMarketSubModule {
  id: number;
  module_id: number;
  name: string;
  type: string;
  content: string;
  video_url: string;
  sequence_number: number;
  points_awarded: number;
  start_date?: string;
  end_date?: string;
}

interface InMarketTableProps {
  moduleId?: string;
  isActive: boolean;
}

// QuestionsView modal (read-only)
function QuestionsView({ isOpen, onClose, questions }: { isOpen: boolean; onClose: () => void; questions: any[] }) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Questions (View Only)</DialogTitle>
        </DialogHeader>
        <div className="space-y-6 overflow-y-auto flex-1 pr-2">
          {questions.length === 0 && <div className="text-gray-500">No questions available.</div>}
          {questions.map((q, qIndex) => (
            <div key={q.id || qIndex} className="space-y-2 p-4 border rounded-lg">
              <div className="font-semibold">Q{qIndex + 1}: {q.question || q.text}</div>
              <div className="ml-4 space-y-1">
                {Array.isArray(q.options) && q.options.map((opt: string, i: number) => (
                  <div key={i} className={q.correctAnswer === opt || q.correctAnswer === i ? "font-bold text-green-700" : ""}>
                    {String.fromCharCode(65 + i)}. {opt}
                    {q.correctAnswer === opt || q.correctAnswer === i ? " (Correct)" : ""}
                  </div>
                ))}
              </div>
              {Array.isArray(q.attachments) && q.attachments.length > 0 && (
                <div className="mt-2">
                  <div className="font-medium">Attachments:</div>
                  <div className="flex gap-2 flex-wrap">
                    {q.attachments.map((att: any, idx: number) => (
                      <div key={idx} className="flex items-center gap-2">
                        {att.type === 'image' && att.preview && (
                          <img src={att.preview} alt="Preview" className="h-10 w-10 object-cover rounded" />
                        )}
                        {att.type === 'pdf' && (
                          <a href={att.url || att.preview} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">PDF</a>
                        )}
                        {att.type === 'audio' && (
                          <audio controls src={att.url || att.preview} className="h-8" />
                        )}
                        <span className="text-xs">{att.file?.name || att.url}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        <DialogFooter className="mt-4">
          <Button type="button" variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function InMarketTable({ moduleId, isActive }: InMarketTableProps) {
  const { toast } = useToast();
  const [modules, setModules] = useState<InMarketSubModule[]>([]);
  const [loading, setLoading] = useState(false);
  const [isQuestionsViewOpen, setIsQuestionsViewOpen] = useState(false);
  const [viewQuestions, setViewQuestions] = useState<any[]>([]);

  useEffect(() => {
    const fetchModules = async () => {
      if (!isActive || !moduleId) {
        setModules([]);
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const data: SubModule[] = await subModuleService.getSubModulesByModuleId(parseInt(moduleId), "in_market");
        const mapped = data.map((item) => ({
          id: Number(item.id),
          module_id: Number(item.moduleId),
          name: item.name || "",
          type: item.type || "",
          content: item.content || "",
          video_url: item.videoUrl || "",
          sequence_number: item.sequenceNumber ?? 0,
          points_awarded: item.pointsAwarded ?? 0,
          start_date: item.startDate,
          end_date: item.endDate,
        }));
        setModules(mapped);
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to fetch in-market modules",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };
    if (isActive && moduleId) {
      fetchModules();
    }
    // Optionally clear data when tab is not active
    if (!isActive) {
      setModules([]);
    }
  }, [isActive, moduleId, toast]);

  const handleViewQuestions = async (subModuleId: number) => {
    try {
      setLoading(true);
      const questions = await questionService.getQuestions(subModuleId);
      setViewQuestions(questions);
      setIsQuestionsViewOpen(true);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch questions",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Spinner label="Loading..." />;
  }

  return (
    <div className="space-y-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Seq</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Content</TableHead>
            <TableHead>Points</TableHead>
            <TableHead>Start Date</TableHead>
            <TableHead>End Date</TableHead>
            <TableHead>Questions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {modules.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} className="text-center text-muted-foreground">
                No in-market submodules found.
              </TableCell>
            </TableRow>
          ) : (
            modules.map((module) => (
              <TableRow key={module.sequence_number + '-' + module.name}>
                <TableCell>{module.sequence_number}</TableCell>
                <TableCell>{module.name}</TableCell>
                <TableCell>{module.type}</TableCell>
                <TableCell>{module.content}</TableCell>
                <TableCell>{module.points_awarded}</TableCell>
                <TableCell>{module.start_date ? module.start_date.split('T')[0] : ''}</TableCell>
                <TableCell>{module.end_date ? module.end_date.split('T')[0] : ''}</TableCell>
                <TableCell>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewQuestions(module.id)}
                    disabled={loading}
                  >
                    View Questions
                  </Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* QuestionsView Modal */}
      <QuestionsView
        isOpen={isQuestionsViewOpen}
        onClose={() => setIsQuestionsViewOpen(false)}
        questions={viewQuestions}
      />
    </div>
  );
}
